import { Helmet } from "react-helmet-async";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Carbonwize</title>
        <meta name="description" content="Privacy Policy for Carbonwize and Sustainwize platforms. Learn how we collect, use, and protect your personal information." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <Navbar />
      
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <article className="legal-content">
            <h1>Privacy Policy</h1>
            
            <p>
              <strong>This Privacy Policy</strong> describes how Carbonwize Co., Ltd. (<strong>"Carbonwize"</strong>, <strong>"we"</strong>, or <strong>"our"</strong>) collects, uses, and protects Personal Information when a <strong>"Visitor"</strong> interacts with our websites (Carbonwize and Sustainwize, collectively the <strong>"Sites"</strong>) or engages with us through events, marketing, or other business activities. The term <strong>"Visitor"</strong> refers to any individual accessing or interacting with the Sites or Carbonwize.
            </p>
            
            <p>
              By accessing or using the Sites, or by clicking <strong>"Agree"</strong> or a similar button, the Visitor acknowledges and agrees to the terms of this Privacy Policy. If the person accepting this Privacy Policy does so on behalf of a company or other legal entity, they represent that they have the authority to bind such entity, and <strong>"Visitor"</strong> shall refer to such entity.
            </p>

            <h2>1. General Information on Data Collection</h2>
            
            <h3>(i) Information Provided by the Visitor</h3>
            
            <p>
              The Sites provide multiple ways for a Visitor to contact Carbonwize, such as web forms, email, or phone, to inquire about the company or its services. For instance, Carbonwize may request certain Personal Information when the Visitor expresses interest in learning more about Carbonwize or Sustainwize, participates in surveys, subscribes to marketing communications, or otherwise initiates contact. Personal Information may also be collected during in-person interactions at events or via calls with Carbonwize representatives.
            </p>
            
            <p><strong>The Personal Information may include:</strong></p>
            <ul>
              <li>Business contact information (such as your name, phone number, email address, and country)</li>
              <li>Professional details (e.g., job title, company name)</li>
              <li>Nature of your communication</li>
              <li>Marketing preferences (such as your contact preferences)</li>
              <li>Any information voluntarily submitted via form free-text fields</li>
            </ul>

            <h3>(ii) Information Automatically Collected via the Sites</h3>
            
            <p>
              When a Visitor accesses the Carbonwize or Sustainwize Sites or interacts with marketing emails, Carbonwize automatically collects certain data using cookies, pixels, web beacons, and similar tracking technologies.
            </p>
            
            <p><strong>The information automatically includes:</strong></p>
            <ul>
              <li><strong>Device data:</strong> IP address, browser type, device identifiers, language settings, mobile network details, request/response logs, referring and exit pages, and recent browsing history</li>
              <li><strong>Usage data:</strong> Pages visited, files accessed, search terms, system configurations, operating system details, and timestamped records of Site or email interaction</li>
            </ul>

            <h2>2. Use of Personal Information</h2>
            
            <p>
              Carbonwize processes Personal Information on the basis of contractual necessity, legitimate interests, consent, or legal obligations, for purposes including but not limited to:
            </p>
            
            <ul>
              <li>Providing and maintaining the Sites and services, including both Carbonwize and Sustainwize platforms</li>
              <li>Fulfilling contractual transactions, such as account creation, subscription setup, payment processing, and billing</li>
              <li>Responding to inquiries and providing requested resources or support</li>
              <li>Sending product updates, service notifications, event invitations, or marketing communications based on the Customer's stated preferences or consent</li>
              <li>Managing event registrations, access control, and non-disclosure agreements</li>
              <li>Improving Site navigation, user experience, and content relevance</li>
              <li>Detecting technical issues, security threats, or performance anomalies</li>
              <li>Generating aggregated usage statistics for internal analysis and platform improvement</li>
              <li>Preventing fraud, misuse, or unauthorized access</li>
              <li>Supporting internal research and development to enhance our services</li>
              <li>Complying with legal obligations or cooperating with public authorities, regulators, or courts as required</li>
            </ul>
            
            <p>
              In addition to Personal Information collected via the Sites, Carbonwize processes <strong>"Customer Data"</strong> submitted by customers through the Carbonwize and Sustainwize platforms. <strong>"Customer Data"</strong> includes content and data input by the customer (e.g., Emission Data, uploaded files, or generated outputs) as part of the SaaS Services. The handling of Customer Data is governed by the applicable <strong>"Terms of Service"</strong> or individual contract between Carbonwize and the customer.
            </p>
            
            <p>
              In the course of these activities, Carbonwize may share Personal Information with trusted third-party service providers, including payment processors (e.g., Pay Solution), cloud infrastructure providers (e.g., AWS), analytics platforms, and communication tools—strictly for the purpose of fulfilling contractual and operational obligations.
            </p>
            
            <p>
              Personal Information is retained only for as long as necessary to fulfill the above purposes. Upon expiration of a lawful basis for processing, such as the termination of a contract, data will be deleted or anonymized unless longer retention is required by applicable law.
            </p>

            <h2>3. Data Retention and Deletion</h2>
            
            <p>
              The personal data of the data subject will be deleted or blocked as soon as the purpose of storage no longer applies. We may store personal data if this has been prescribed by the Kingdom of Thailand regulations, laws, or other regulations to which the person responsible is subject. The data will also be blocked or deleted if a storage period prescribed by the standards mentioned expires unless there is a need for further storage of the data for the conclusion or performance of a contract.
            </p>

            <h2>4. Website Access and Log Files</h2>
            
            <p>
              Each time a Visitor accesses the Sites, Carbonwize collects technical log files, including browser and OS type, referring URL, pages accessed, access timestamp, IP address, and internet provider. These logs help us optimize performance, detect cyber threats, and ensure system security. This information is anonymized for statistical analysis and stored separately from Personal Information.
            </p>

            <h2>5. Use of Third-Party Tools</h2>
            
            <p>Carbonwize may use third-party service providers such as:</p>
            <ul>
              <li><strong>AWS</strong> (infrastructure)</li>
              <li><strong>Pay Solutions</strong> (payment processing)</li>
              <li><strong>Freshdesk, Pipedrive</strong> (marketing and support tools)</li>
            </ul>
            
            <p>
              These providers may access transaction information but do not sell or share it for advertising. Limited data (e.g., zip code, shipping info) may be shared with merchants for tax and delivery purposes. Identity verification providers may be used to prevent fraud.
            </p>

            <h2>6. Cookie Usage</h2>
            
            <p>
              Carbonwize uses cookies and similar technologies (e.g., web beacons, pixels) to enhance website functionality, personalize content, and analyze usage. These include:
            </p>
            
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Essential Cookies</strong></td>
                    <td>Required for the basic functionality of the Sites, such as session management and security.</td>
                    <td>Session cookie, login status, form submission, payment</td>
                  </tr>
                  <tr>
                    <td><strong>Language Cookies</strong></td>
                    <td>Remember language preferences set by the Visitor.</td>
                    <td>Language preference storage</td>
                  </tr>
                  <tr>
                    <td><strong>Analytics Cookies</strong></td>
                    <td>Help us understand how Visitors interact with the Sites to improve performance and usability.</td>
                    <td>Google Analytics, Facebook Pixel</td>
                  </tr>
                  <tr>
                    <td><strong>Marketing Cookies</strong></td>
                    <td>Enable us to deliver targeted marketing or track campaign effectiveness.</td>
                    <td>Facebook Pixel, Pipedrive, Freshdesk</td>
                  </tr>
                  <tr>
                    <td><strong>Third-Party Cookies</strong></td>
                    <td>Set by third-party services integrated into our Sites. These may track your activity across websites.</td>
                    <td>Google, Facebook, Freshdesk, Pipedrive</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p>By continuing to use our Sites, the Visitor consents to our use of cookies as described.</p>

            <h2>7. Changes to This Policy</h2>
            
            <p>
              Carbonwize may update this Privacy Policy to reflect changes in legal requirements or business practices. Material changes will be communicated through the Sites or email. The latest version will always be available at <a href="https://carbonwize.io">www.carbonwize.io</a>.
            </p>

            <h2>8. Contact Us</h2>
            
            <p>
              If you have questions or requests regarding this Privacy Policy or your Personal Information, contact: <a href="mailto:support@carbonwize.io">support@carbonwize.io</a>
            </p>
          </article>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
